import { Injectable } from '@angular/core';
import { Observable, from, map } from 'rxjs';

export interface SheetRow {
  [key: string]: string;
}

export interface SheetTableData {
  columns: string[];
  rows: SheetRow[];
}

@Injectable({
  providedIn: 'root'
})
export class SheetTableService {
  loadTableFromCsvUrl(csvUrl: string): Observable<SheetTableData> {
    return from(this.fetchCsv(csvUrl)).pipe(
      map((csvText) => this.parseCsvToTableData(csvText))
    );
  }

  private async fetchCsv(csvUrl: string): Promise<string> {
    const response = await fetch(csvUrl);
    if (!response.ok) {
      throw new Error(`Failed to load CSV: ${response.status}`);
    }

    return response.text();
  }

  private parseCsvToTableData(csvText: string): SheetTableData {
    const allRows = this.parseCsv(csvText)
      .map((row) => row.map((cell) => cell.trim()))
      .filter((row) => row.some((cell) => cell.length > 0));

    if (allRows.length === 0) {
      return { columns: [], rows: [] };
    }

    const columns = allRows[0];
    const rows: SheetRow[] = allRows
      .slice(1)
      .map((values) => this.toRow(columns, values))
      .filter((row) => Object.values(row).some((value) => value.trim().length > 0));

    return { columns, rows };
  }

  private toRow(columns: string[], values: string[]): SheetRow {
    const row: SheetRow = {};

    columns.forEach((column, index) => {
      row[column] = values[index]?.trim() ?? '';
    });

    return row;
  }

  private parseCsv(csvText: string): string[][] {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let current = '';
    let insideQuotes = false;

    for (let i = 0; i < csvText.length; i++) {
      const char = csvText[i];

      if (char === '"') {
        if (insideQuotes && csvText[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        currentRow.push(current);
        current = '';
      } else if ((char === '\n' || char === '\r') && !insideQuotes) {
        if (char === '\r' && csvText[i + 1] === '\n') {
          i++;
        }
        currentRow.push(current);
        rows.push(currentRow);
        currentRow = [];
        current = '';
      } else {
        current += char;
      }
    }

    currentRow.push(current);
    rows.push(currentRow);

    return rows;
  }
}
